import { useEffect, useMemo, useRef } from "react";
import { View, StyleSheet } from "react-native";
import { TextInput, Text } from "react-native-paper";

import {
    type BpVerdict,
    classifyBp,
    bpStatusValue,
    statusQuestionIdFromConfig,
    BP_VERDICT_MESSAGE
} from "./bp-classify";
import type { QuestionComponentProps } from "./types";

interface BPValue {
    systolic: string;
    diastolic: string;
}

function parse(value: string | undefined): BPValue {
    if (!value) {
        return { systolic: "", diastolic: "" };
    }
    try {
        return JSON.parse(value);
    } catch {
        return { systolic: "", diastolic: "" };
    }
}

const VERDICT_STYLES: Record<BpVerdict, { stripeBg: string; stripeText: string; badgeBg: string }> =
    {
        crisis: { stripeBg: "#FEF2F2", stripeText: "#B91C1C", badgeBg: "#EF4444" },
        high: { stripeBg: "#FEF9C3", stripeText: "#B45309", badgeBg: "#F59E0B" },
        low: { stripeBg: "#EFF6FF", stripeText: "#1D4ED8", badgeBg: "#3B82F6" }
    };

export function BloodPressureQuestion({
    question,
    value,
    onChange,
    readOnly,
    onFocus,
    onChildAnswerChange
}: QuestionComponentProps) {
    const bp = parse(value);

    const update = (field: keyof BPValue, val: string) => {
        onChange(JSON.stringify({ ...bp, [field]: val }));
    };

    const verdict = useMemo(
        () => classifyBp(bp.systolic, bp.diastolic),
        [bp.systolic, bp.diastolic]
    );

    // If the form wires a companion "BP status" question (via this question's
    // config), mirror the verdict — crisis / high / low / normal — into its
    // answer so branching rules can gate follow-ups on a plain value. BP's own
    // answer is `{"systolic","diastolic"}` JSON, which the numeric branching
    // operators can't read; the status word is what they compare against.
    // No companion configured → no-op (safe in environments without one).
    const statusQuestionId = statusQuestionIdFromConfig(question.config);
    const statusValue = bpStatusValue(bp.systolic, bp.diastolic);
    const writeRef = useRef(onChildAnswerChange);
    writeRef.current = onChildAnswerChange;
    useEffect(() => {
        if (statusQuestionId) {
            writeRef.current?.(statusQuestionId, statusValue);
        }
    }, [statusQuestionId, statusValue]);

    return (
        <View>
            <View style={styles.row}>
                <TextInput
                    mode="outlined"
                    value={bp.systolic}
                    onChangeText={(v) => update("systolic", v)}
                    disabled={readOnly}
                    keyboardType="numeric"
                    placeholder="Sys"
                    onFocus={onFocus}
                    style={styles.input}
                    dense
                />
                <Text style={styles.slash}>/</Text>
                <TextInput
                    mode="outlined"
                    value={bp.diastolic}
                    onChangeText={(v) => update("diastolic", v)}
                    disabled={readOnly}
                    keyboardType="numeric"
                    placeholder="Dia"
                    onFocus={onFocus}
                    style={styles.input}
                    dense
                />
            </View>

            {verdict ? (
                <View
                    style={[styles.stripe, { backgroundColor: VERDICT_STYLES[verdict].stripeBg }]}
                >
                    <Text
                        style={[styles.badge, { backgroundColor: VERDICT_STYLES[verdict].badgeBg }]}
                    >
                        {verdict.toUpperCase()}
                    </Text>
                    <Text
                        style={[styles.stripeText, { color: VERDICT_STYLES[verdict].stripeText }]}
                    >
                        Blood pressure {bp.systolic}/{bp.diastolic} mmHg —{" "}
                        {BP_VERDICT_MESSAGE[verdict]}
                    </Text>
                </View>
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8
    },
    input: {
        backgroundColor: "#fff",
        width: 80
    },
    slash: {
        fontSize: 20,
        color: "#666"
    },
    stripe: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        borderRadius: 6,
        padding: 8,
        marginTop: 6
    },
    badge: {
        color: "#fff",
        fontSize: 11,
        fontWeight: "700",
        letterSpacing: 0.5,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 5,
        overflow: "hidden"
    },
    stripeText: {
        flex: 1,
        fontSize: 12
    }
});
